import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, message, Card, Typography, Space } from 'antd';
import { LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './ResetPassword.css';
import { HERO_BACKGROUND_MP4 } from '../constants/publicAssets';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [bgVideoBroken, setBgVideoBroken] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 确保视频在移动端自动播放
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // 设置视频属性以支持自动播放
      video.muted = true;
      video.playsInline = true;
      
      // 尝试播放视频
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // 播放成功，隐藏播放控件
            video.style.pointerEvents = 'none';
          })
          .catch((error) => {
            console.log('自动播放失败，可能需要用户交互:', error);
            // 如果自动播放失败，在用户点击页面时尝试播放
            const handleInteraction = () => {
              video.play().catch(err => console.log('播放失败:', err));
              document.removeEventListener('touchstart', handleInteraction);
              document.removeEventListener('click', handleInteraction);
            };
            document.addEventListener('touchstart', handleInteraction, { once: true });
            document.addEventListener('click', handleInteraction, { once: true });
          });
      }
    }
  }, []);

  useEffect(() => {
    // 验证令牌
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setTokenValid(false);
        message.error('重置密码链接无效');
        return;
      }

      try {
        const response = await fetch(`/api/auth/reset-password/${token}`);
        const data = await response.json();

        if (response.ok) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
          message.error(data.error || '重置密码链接无效或已过期');
        }
      } catch (error) {
        console.error('Validate token error:', error);
        setTokenValid(false);
        message.error('验证失败，请稍后重试');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: values.password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success(data.message || '密码重置成功，请使用新密码登录');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        message.error(data.error || '重置失败，请稍后重试');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      message.error('重置失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const videoBackground = (
    <>
      <video 
        ref={videoRef}
        className="reset-password-video-background" 
        autoPlay 
        muted 
        playsInline
        loop
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onError={() => setBgVideoBroken(true)}
        style={{
          pointerEvents: 'none',
          display: bgVideoBroken ? 'none' : undefined
        } as React.CSSProperties}
        {...({
          'webkit-playsinline': true,
          'x5-playsinline': true,
          'x5-video-player-type': 'h5',
          ...(true ? { 'x5-video-player-fullscreen': 'true' } : {})
        } as any)}
      >
        <source src={HERO_BACKGROUND_MP4} type="video/mp4" />
        您的浏览器不支持视频播放。
      </video>
      <div className="reset-password-video-overlay"></div>
    </>
  );

  if (validating) {
    return (
      <div className="reset-password-container">
        {videoBackground}
        <Card className="reset-password-card">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text>正在验证重置密码链接...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        {videoBackground}
        <Card className="reset-password-card">
          <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
            <div>
              <Title level={3}>链接无效</Title>
              <Text type="secondary">重置密码链接无效或已过期</Text>
            </div>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/forgot-password')}
            >
              重新申请重置密码
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      {videoBackground}
      <Card className="reset-password-card">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="reset-password-header">
            <Title level={3}>重置密码</Title>
            <Text type="secondary">请输入您的新密码</Text>
          </div>

          <Form
            form={form}
            name="reset-password"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="password"
              label="新密码"
              rules={[
                { required: true, message: '请输入新密码!' },
                { min: 6, message: '密码长度至少6位!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请输入新密码（至少6位）"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请再次输入密码!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="请再次输入新密码"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                {loading ? '重置中...' : '重置密码'}
              </Button>
            </Form.Item>
          </Form>

          <div className="reset-password-footer">
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              style={{ padding: 0 }}
            >
              返回登录
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ResetPassword;

